import type { Backup, ImportMode, ParseFailure } from '../../domain/backup';
import { previewImport } from '../../domain/backup';
import type { Station } from '../../domain/stations';
import { type Lang, plural, type TranslationKey, translate } from '../../i18n';
import { el } from '../../lib/dom';
import { joinMeta } from '../format';
import { createModal } from './modal';

/**
 * Predogled uvoza in potrditev odstranitve prednaloženih postaj.
 *
 * **Uvoz brez predogleda je nepovraten poseg v edine podatke, ki jih
 * uporabnik ima** (T2 §7, točka 3). Zato predogled ni dodatek k uvozu,
 * ampak njegov del: pove, koliko postaj je v datoteki, koliko jih je novih,
 * koliko že imate in — kar je najlažje spregledati — ali bo uvoz **prepisal
 * nastavitve**.
 *
 * Privzeti način je **združi** (T2 §7, točka 4). Zamenjava je nepovratna in
 * ne sme biti privzeti klik.
 */

export interface BackupDialogDeps {
  /** Trenutni seznam — predogled mora primerjati z njim, ne z ničemer. */
  stations(): readonly Station[];
  onImport(backup: Backup, mode: ImportMode): void;
  onClearSeed(): void;
}

export interface BackupDialog {
  openImport(backup: Backup): void;
  showFailure(failure: ParseFailure): void;
  confirmClearSeed(): void;
  setLang(lang: Lang): void;
}

export function createBackupDialog(root: HTMLElement, deps: BackupDialogDeps): BackupDialog {
  let lang: Lang = 'en';

  /* ── Predogled uvoza ────────────────────────────────────────────── */

  const importModal = createModal('nw-import-title');

  const summary = el('p', { class: 'desc' });
  const settingsWarning = el('p', { class: 'hint', hidden: '' });

  /**
   * Način je izbira enega iz nabora, zato radio in ne dva preklopna gumba:
   * bralnik pove »1 od 2«, tipkovnica preklaplja s puščicami in vzorec APG
   * dobimo zastonj. Vrstici sta `.set-row` v `.ao-set` — ista komponenta
   * kot v Nastavitvah, ker je oblika ista: naslov, opis, kontrolnik.
   */
  function modeRow(id: string): {
    row: HTMLDivElement;
    input: HTMLInputElement;
    title: HTMLDivElement;
    desc: HTMLDivElement;
  } {
    const input = el('input', { type: 'radio', name: 'nw-import-mode', id });
    const title = el('div', { class: 'set-title' });
    const desc = el('div', { class: 'set-desc' });
    const row = el('div', { class: 'set-row' }, [
      el('label', { class: 'set-main', for: id }, [title, desc]),
      el('div', { class: 'set-ctrl' }, [input]),
    ]);
    return { row, input, title, desc };
  }

  const merge = modeRow('nw-mode-merge');
  const replace = modeRow('nw-mode-replace');
  merge.input.checked = true;

  const modes = el('div', { class: 'ao-set', role: 'radiogroup' }, [merge.row, replace.row]);
  const modeMerge = merge.input;
  const modeReplace = replace.input;

  const failure = el('p', { class: 'hint error', role: 'alert', hidden: '' });
  importModal.body.append(summary, settingsWarning, modes, failure);

  /* ── Potrditev odstranitve seeda ────────────────────────────────── */

  const seedModal = createModal('nw-seed-title');
  const seedBody = el('p', { class: 'desc' });
  seedModal.body.append(seedBody);
  seedModal.confirm.classList.remove('primary');
  seedModal.confirm.classList.add('danger');

  root.append(importModal.scrim, seedModal.scrim);

  /* ── Stanje ─────────────────────────────────────────────────────── */

  let pending: Backup | null = null;

  function currentMode(): ImportMode {
    return modeReplace.checked ? 'replace' : 'merge';
  }

  function renderPreview(): void {
    if (pending === null) return;
    const view = previewImport(deps.stations(), pending, currentMode());

    /**
     * ⚠️ **Vsako število gre skozi `Intl.PluralRules`** (P4 §9). Slovenščina
     * ima dvojino: 1 postaja · 2 postaji · 3 postaje · 5 postaj. Ob menjavi
     * načina se podvojene spremenijo na 0, zato se predogled izriše v celoti
     * in ne posodablja po delih.
     */
    summary.textContent = joinMeta([
      plural(lang, 'backup.preview.total', view.total),
      plural(lang, 'backup.preview.added', view.added),
      plural(lang, 'backup.preview.duplicate', view.duplicate),
    ]);

    settingsWarning.textContent = translate(lang, 'backup.preview.settings');
    settingsWarning.toggleAttribute('hidden', !view.replacesSettings);
  }

  for (const input of [modeMerge, modeReplace]) {
    input.addEventListener('change', renderPreview);
  }

  importModal.confirm.addEventListener('click', () => {
    if (pending === null) return;
    deps.onImport(pending, currentMode());
    pending = null;
    importModal.hide();
  });

  seedModal.confirm.addEventListener('click', () => {
    deps.onClearSeed();
    seedModal.hide();
  });

  function applyLang(): void {
    importModal.title.textContent = translate(lang, 'backup.import.title');
    importModal.close.setAttribute('aria-label', translate(lang, 'dialog.close'));
    importModal.cancel.textContent = translate(lang, 'dialog.cancel');
    importModal.confirm.textContent = translate(lang, 'backup.import.action');

    merge.title.textContent = translate(lang, 'backup.mode.merge');
    merge.desc.textContent = translate(lang, 'backup.mode.merge.desc');
    replace.title.textContent = translate(lang, 'backup.mode.replace');
    replace.desc.textContent = translate(lang, 'backup.mode.replace.desc');
    modes.setAttribute('aria-label', translate(lang, 'backup.import.title'));

    seedModal.title.textContent = translate(lang, 'settings.seed.confirm.title');
    seedModal.close.setAttribute('aria-label', translate(lang, 'dialog.close'));
    seedModal.cancel.textContent = translate(lang, 'dialog.cancel');
    seedModal.confirm.textContent = translate(lang, 'settings.seed.action');
    seedBody.textContent = translate(lang, 'settings.seed.confirm.body');
  }

  return {
    setLang(next) {
      lang = next;
      applyLang();
      renderPreview();
    },

    openImport(backup) {
      pending = backup;
      // Vsak uvoz se začne pri »združi«, tudi če je prejšnji končal pri
      // »zamenjaj«: podedovana zamenjava bi bila nepovratna po pomoti.
      modeMerge.checked = true;
      failure.toggleAttribute('hidden', true);
      summary.toggleAttribute('hidden', false);
      modes.toggleAttribute('hidden', false);
      settingsWarning.toggleAttribute('hidden', true);
      importModal.confirm.toggleAttribute('hidden', false);
      renderPreview();
      importModal.show(modeMerge);
    },

    showFailure(reason) {
      // Neznana oblika → jasna napaka, brez ugibanja (T2 §7, točka 1).
      pending = null;
      summary.toggleAttribute('hidden', true);
      settingsWarning.toggleAttribute('hidden', true);
      modes.toggleAttribute('hidden', true);
      importModal.confirm.toggleAttribute('hidden', true);
      failure.textContent = translate(lang, `backup.error.${reason.reason}` as TranslationKey);
      failure.toggleAttribute('hidden', false);
      importModal.show(importModal.cancel);
    },

    confirmClearSeed() {
      // Fokus na »prekliči«: privzeti cilj tipke Enter pri nepovratnem
      // dejanju ne sme biti to dejanje.
      seedModal.show(seedModal.cancel);
    },
  };
}
